// dbg-r5 Bug C — single-row inventory drilldown page mounted at
// /inventory/:stockId. Renders the stock summary + a paged movement
// timeline. Replaces the previous "no detail page exists" spec_gap.

import { Link, useParams } from 'react-router-dom';

import { cn, PageHeader } from '@printway/ui';

import { useTranslation } from '../lib/i18n/index.js';
import { STATUS_PILL_CLASS } from '../lib/ui/statusPill.js';
import {
  useStockDetail,
  type MovementRow,
} from '../features/inventory/api.js';

function formatTime(ms: number, locale: string): string {
  return new Date(ms).toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusPill({
  isLow,
  isOut,
}: {
  isLow: boolean;
  isOut: boolean;
}): JSX.Element {
  const { t } = useTranslation('inventory');
  if (isOut) {
    return (
      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${STATUS_PILL_CLASS.danger}`}>
        {t('detail.statusOut')}
      </span>
    );
  }
  if (isLow) {
    return (
      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${STATUS_PILL_CLASS.progress}`}>
        {t('detail.statusLow')}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${STATUS_PILL_CLASS.success}`}>
      {t('detail.statusOk')}
    </span>
  );
}

function MovementsTable({
  rows,
  total,
}: {
  rows: MovementRow[];
  total: number;
}): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted">
        {t('detail.movementsEmpty')}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <table className="w-full table-auto text-sm">
          <thead className="border-b border-pw-border text-xs uppercase tracking-wider text-pw-text-muted">
            <tr>
              <th className="px-2 py-2 text-left">{t('detail.movementHeaderTime')}</th>
              <th className="px-2 py-2 text-left">{t('detail.movementHeaderType')}</th>
              <th className="px-2 py-2 text-right">{t('detail.movementHeaderQty')}</th>
              <th className="px-2 py-2 text-left">
                {t('detail.movementHeaderBeforeAfter')}
              </th>
              <th className="px-2 py-2 text-left">{t('detail.movementHeaderReason')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr
                key={m.movement_id}
                data-testid={`inventory-detail-movement-${m.movement_id}`}
                className="border-b border-pw-border"
              >
                <td className="px-2 py-2 font-mono text-xs text-pw-text-muted">
                  {formatTime(m.occurred_at, i18n.language)}
                </td>
                <td className="px-2 py-2">
                  <span
                    className={cn(
                      'inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold',
                      m.type.startsWith('transfer_')
                        ? STATUS_PILL_CLASS.info
                        : m.type === 'adjust_increase'
                          ? STATUS_PILL_CLASS.success
                          : m.type === 'adjust_decrease'
                            ? STATUS_PILL_CLASS.danger
                            : STATUS_PILL_CLASS.done,
                    )}
                  >
                    {t(`detail.movementType.${m.type}`, { defaultValue: m.type })}
                  </span>
                </td>
                <td className="px-2 py-2 text-right font-mono">{m.quantity}</td>
                <td className="px-2 py-2 font-mono text-xs">
                  {m.before.on_hand} → {m.after.on_hand}
                </td>
                <td className="px-2 py-2 text-pw-text-muted">{m.reason ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="text-xs text-pw-text-muted">
        {t('detail.movementsShowing', { shown: rows.length, total })}
      </span>
    </div>
  );
}

export function InventoryDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  const { stockId } = useParams<{ stockId: string }>();
  const query = useStockDetail(stockId);

  if (query.isLoading) {
    return (
      <main className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary" />
          <p className="text-sm text-pw-text-muted">{t('detail.loading')}</p>
        </div>
      </main>
    );
  }

  if (query.isError || !query.data) {
    return (
      <main className="px-6 py-10">
        <div
          data-testid="inventory-detail-not-found"
          className="mx-auto max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 text-center shadow-sm"
        >
          <h2 className="text-lg font-semibold text-pw-text">
            {t('detail.notFoundTitle')}
          </h2>
          <p className="mt-2 text-sm text-pw-text-muted">
            {t('detail.notFoundBody')}
          </p>
          <Link
            to="/inventory"
            className="mt-4 inline-flex rounded-md bg-pw-primary px-4 py-2 text-sm font-medium text-white hover:bg-pw-primary-strong"
          >
            {t('detail.backToList')}
          </Link>
        </div>
      </main>
    );
  }

  const { stock, movements, movement_total } = query.data;

  return (
    <main
      data-testid="inventory-detail-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <div className="flex items-center justify-between">
        <Link
          to="/inventory"
          data-testid="inventory-detail-back-link"
          className="text-sm text-pw-primary hover:underline"
        >
          {t('detail.backToList')}
        </Link>
        <StatusPill
          isLow={stock.is_low_stock}
          isOut={stock.is_out_of_stock}
        />
      </div>

      <PageHeader
        className="gap-2 rounded-xl border border-pw-border bg-pw-bg p-4"
        title={stock.product_name ?? '—'}
        subtitle={
          <span className="text-xs">
            {stock.variant_label ?? '—'} · {stock.variant_sku ?? '—'} ·{' '}
            {stock.warehouse_name ?? '—'}
          </span>
        }
      />

      <section
        data-testid="inventory-detail-summary"
        className="rounded-xl border border-pw-border bg-pw-bg p-4"
      >
        <h3 className="mb-3 text-sm font-semibold text-pw-text">
          {t('detail.summary')}
        </h3>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-4">
          <Field label={t('detail.fieldOnHand')} value={stock.on_hand} />
          <Field label={t('detail.fieldReserved')} value={stock.reserved} />
          <Field
            label={t('detail.fieldAvailable')}
            value={
              <span
                className={cn(
                  'font-mono',
                  stock.is_out_of_stock
                    ? 'text-red-700'
                    : stock.is_low_stock
                      ? 'text-yellow-700'
                      : 'text-pw-text',
                )}
              >
                {stock.available}
              </span>
            }
          />
          <Field
            label={t('detail.fieldLowThreshold')}
            value={stock.low_stock_threshold}
          />
          <Field
            label={t('detail.fieldVersion')}
            value={<span className="font-mono">v{stock.version}</span>}
          />
          <Field
            label={t('detail.fieldUpdatedAt')}
            value={
              <span className="font-mono text-xs">
                {formatTime(stock.last_updated_at, i18n.language)}
              </span>
            }
          />
        </dl>
      </section>

      <section
        data-testid="inventory-detail-movements"
        className="rounded-xl border border-pw-border bg-pw-bg p-4"
      >
        <h3 className="mb-3 text-sm font-semibold text-pw-text">
          {t('detail.movementsTitle')}
        </h3>
        <MovementsTable rows={movements} total={movement_total} />
      </section>
    </main>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10px] uppercase tracking-wider text-pw-text-muted">
        {label}
      </dt>
      <dd className="text-sm text-pw-text">{value}</dd>
    </div>
  );
}

export default InventoryDetailPage;
